import React from "react";
import { NotesList } from "./NotesList";
import {
  useGetProviderNotesQuery,
  useCreateProviderNoteMutation,
  useUpdateProviderNoteMutation,
  useDeleteProviderNoteMutation,
} from "../../redux/reduxQuery/providerNotesApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useUser } from "../../hooks/useLogin";

const NOTE_TYPE_LABELS: Record<string, string> = {
  sender: "Sender's Notes",
  followUp: "Follow Up Notes",
  vetting: "Vetting Notes",
  connection: "Connection Notes",
};

interface ProviderNotesListProps {
  providerId: string;
  noteType: "sender" | "followUp" | "vetting" | "connection";
  title?: string;
  hideBanned?: boolean;
}

export const ProviderNotesList = ({
  providerId,
  noteType,
  title,
  hideBanned = false,
}: ProviderNotesListProps) => {
  const user = useUser();
  const isAdmin = user?.isAdmin() || false;
  const { data: allNotes = [], isLoading } = useGetProviderNotesQuery(
    providerId ? { providerId, noteType } : skipToken
  );
  const notes = hideBanned ? allNotes.filter((n) => !n.isBanned) : allNotes;
  const [createNote, { isLoading: isCreating }] =
    useCreateProviderNoteMutation();
  const [updateNote] = useUpdateProviderNoteMutation();
  const [deleteNote] = useDeleteProviderNoteMutation();

  const displayTitle = title || NOTE_TYPE_LABELS[noteType] || noteType;

  const handleCreate = async (body: string) => {
    await createNote({ providerId, noteType, body }).unwrap();
  };

  const handleUpdate = async (
    noteId: string,
    fields: { body?: string; isHighlighted?: boolean; isBanned?: boolean }
  ) => {
    await updateNote({ providerId, noteId, ...fields }).unwrap();
  };

  const handleDelete = async (noteId: string) => {
    await deleteNote({ providerId, noteId }).unwrap();
  };

  return (
    <NotesList
      notes={notes}
      isLoading={isLoading}
      isCreating={isCreating}
      title={displayTitle}
      defaultExpanded={noteType === "connection"}
      currentUserId={user?.id}
      isAdmin={isAdmin}
      onCreateNote={handleCreate}
      onUpdateNote={handleUpdate}
      onDeleteNote={handleDelete}
    />
  );
};
